import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";
import { isUserAdmin } from "../../utils/userUtils";

import LeagueMatchList from "./LeagueMatchList";
import LeaguePlayerManager from "./LeaguePlayerManager";
import LeagueCostSplitTab from "./LeagueCostSplitTab";

const LeagueTeamDetail = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const highlightMatchId = searchParams.get("matchId") ? Number(searchParams.get("matchId")) : null;
    const api = useApiStable();
    const { user } = useAuth();
    const [team, setTeam] = useState(null);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchesLoading, setMatchesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("matches");
    const [followBusy, setFollowBusy] = useState(false);

    const toggleFollow = async () => {
        if (!team || followBusy) return;
        setFollowBusy(true);
        try {
            const updated = team.followedByCurrentUser
                ? await api.delete(`/league/teams/${team.id}/follow`)
                : await api.post(`/league/teams/${team.id}/follow`);
            setTeam(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setFollowBusy(false);
        }
    };

    const loadTeam = useCallback(() => {
        return api.get(`/league/teams/${id}`)
            .then(setTeam)
            .catch(err => setError(err.message));
    }, [id]);

    const loadMatches = useCallback(() => {
        setMatchesLoading(true);
        return api.get(`/league/teams/${id}/matches`)
            .then(setMatches)
            .catch(err => setError(err.message))
            .finally(() => setMatchesLoading(false));
    }, [id]);

    // Stilles Neuladen (z. B. nach Zu-/Absage): kein Lade-Zustand, damit die Liste gemountet bleibt
    // und Scrollposition sowie aufgeklappte Spiele erhalten bleiben
    const refreshMatches = useCallback(() => {
        return api.get(`/league/teams/${id}/matches`)
            .then(setMatches)
            .catch(err => setError(err.message));
    }, [id]);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadTeam(), loadMatches()])
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="groupphase-section"><p>Lade Mannschaft...</p></div>;
    if (error) return <div className="groupphase-section"><p style={{ color: "red" }}>Fehler: {error}</p></div>;
    if (!team) return null;

    const isCaptain = team.captainForCurrentUser;
    const isAdmin = isUserAdmin(user);
    const canManage = isCaptain || isAdmin;
    const isMember = team.players.some(p => p.uuid === user?.personUuid);
    const canSeeMembers = isMember || isAdmin;

    return (
        <div className="groupphase-section">
            <div style={{ marginBottom: 16 }}>
                <Link to="/mannschaften" style={{ fontSize: 13, color: "#4CAF50" }}>
                    ← Alle Mannschaften
                </Link>
            </div>

            <div style={{ marginBottom: 20 }}>
                <h2 style={{ marginBottom: 4 }}>{team.name}</h2>
                <div style={{ fontSize: 14, color: "#666" }}>Saison {team.season}</div>

                {(team.captainsName || team.captainsEmail) && (
                    <div style={{ fontSize: 14, color: "#555", marginTop: 6 }}>
                        <strong>Mannschaftsführer:</strong>{" "}
                        {team.captainsName || ""}
                        {team.captainsEmail && (
                            <>
                                {" "}
                                <a
                                    href={`mailto:${team.captainsEmail}`}
                                    style={{ color: "#1565c0", textDecoration: "none" }}
                                >
                                    {team.captainsEmail}
                                </a>
                            </>
                        )}
                    </div>
                )}

                {(team.tablePosition != null || team.tablePoints) && (
                    <div style={{ fontSize: 14, color: "#555", marginTop: 4 }}>
                        <strong>Tabelle:</strong>{" "}
                        {team.tablePosition != null && `${team.tablePosition}. Platz`}
                        {team.tablePoints && ` · ${team.tablePoints} Punkte`}
                    </div>
                )}

                {team.externalTeamId && (
                    <div style={{ marginTop: 6 }}>
                        <a
                            href={`https://tnb.liga.nu/cgi-bin/WebObjects/nuLigaTENDE.woa/wa/teamPortrait?team=${team.externalTeamId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 13, color: "#1565c0", textDecoration: "none" }}
                        >
                            TNB-Seite ↗
                        </a>
                    </div>
                )}
                {isCaptain && (
                    <div style={{ marginTop: 8 }}>
                        <span className="groupphase-status-badge groupphase-status-badge--success">
                            Sie sind Mannschaftsführer
                        </span>
                    </div>
                )}

                {user && !isCaptain && !isMember && (
                    <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                        <button
                            onClick={toggleFollow}
                            disabled={followBusy}
                            style={{
                                padding: "6px 14px",
                                fontSize: 13,
                                cursor: followBusy ? "default" : "pointer",
                                border: team.followedByCurrentUser ? "1px solid #bbb" : "1px solid #4CAF50",
                                borderRadius: 6,
                                backgroundColor: team.followedByCurrentUser ? "#f5f5f5" : "#4CAF50",
                                color: team.followedByCurrentUser ? "#555" : "#fff",
                            }}
                        >
                            {followBusy
                                ? "..."
                                : team.followedByCurrentUser
                                    ? "Nicht mehr folgen"
                                    : "Folgen"}
                        </button>
                        {team.followedByCurrentUser && (
                            <span className="groupphase-status-badge">Gefolgt</span>
                        )}
                    </div>
                )}
            </div>

            {/* Tab navigation */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, borderBottom: "2px solid #eee" }}>
                {["matches", ...(canSeeMembers ? ["players", "costs"] : [])].map(tab => {
                    const label = tab === "matches"
                        ? `Spieltermine (${matches.length})`
                        : tab === "players"
                            ? `Spieler (${team.players.length})`
                            : "Kosten";
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: "8px 16px",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: activeTab === tab ? "bold" : "normal",
                                color: activeTab === tab ? "#4CAF50" : "#555",
                                borderBottom: activeTab === tab ? "2px solid #4CAF50" : "2px solid transparent",
                                marginBottom: -2,
                            }}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            {activeTab === "matches" && (
                <LeagueMatchList
                    teamId={id}
                    matches={matches}
                    loading={matchesLoading}
                    currentUser={user}
                    isMember={isMember}
                    isCaptain={canManage}
                    canSeeLineup={canSeeMembers}
                    onAvailabilityChange={refreshMatches}
                    highlightMatchId={highlightMatchId}
                />
            )}

            {activeTab === "players" && (
                <LeaguePlayerManager
                    team={team}
                    canManage={canManage}
                    isAdmin={isAdmin}
                    canSeeMembers={canSeeMembers}
                    onTeamChange={loadTeam}
                />
            )}

            {activeTab === "costs" && (
                <LeagueCostSplitTab teamId={id} matches={matches} team={team} />
            )}
        </div>
    );
};

export default LeagueTeamDetail;
